import { Component, OnInit } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { filter } from "rxjs/operators";
import { AgsAuthService } from "@ags/core";
import { ApplicationInsights, IPageViewTelemetry } from "@microsoft/applicationinsights-web";
import { NgbTooltipConfig } from "@ng-bootstrap/ng-bootstrap";
import { environment } from "../environments/environment";
import { MenuService } from "./shared/menu.service";
import { AppInsightService } from "./shared/appinsight.service";
import { ApiService } from "./services";
import { SharedDataService } from "./services/sharedDataService";
import { AgsSideNavItem } from "@ags/common-ui";
import { IUserProfile } from "./npr-request.model";

@Component({
  standalone: false,
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  betEnabled = environment.betEnabled;
  // When deployed, Billing Meter pages live in a separate BET UI app. Falls back
  // to the in-app /prep/status route for local dev / envs without BetUiUrl set.
  betUiUrl: string | null = environment.betUiUrl || null;
  isTestEnv = environment.isTestEnv;
  moduleNavItems: AgsSideNavItem[];
  userProfile: IUserProfile;

  constructor(
    private titleService: Title,
    private menuService: MenuService,
    private ngbTooltipConfig: NgbTooltipConfig,
    private router: Router,
    private authService: AgsAuthService,
    private sharedDataService: SharedDataService,
    private apiService: ApiService,
    protected appInsightService: AppInsightService,
    private readonly appInsights: ApplicationInsights
  ) {
    this.initUserProfile();
    this.ngbTooltipConfig.triggers = "hover focus";
    this.ngbTooltipConfig.closeDelay = 100;
    if (!environment.appInsightsInstrumentationKey) {
      return;
    }

    this.appInsights.config.instrumentationKey = environment.appInsightsInstrumentationKey;
    this.appInsights.loadAppInsights();
    this.authService.getAccount().then((user) => {
      if (user) {
        this.appInsights.setAuthenticatedUserContext(user.username);
      } else {
        this.appInsights.setAuthenticatedUserContext("anonymous");
      }
    });
  }

  initUserProfile() {
    this.apiService.getUserProfile().then((res) => {
      this.userProfile = res;
      this.sharedDataService.setUserProfile(res);
    });
  }

  getModuleNavItems() {
    return this.menuService.getModuleNavItems();
  }

  hasModuleNavItems(): boolean {
    return !!this.getModuleNavItems()?.length;
  }

  ngOnInit() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((_) => {
      const title = this.getTitleFromRouteData("title");
      this.titleService.setTitle(`${title} - Region Access and Quota`);
      if (this.router.routerState.snapshot.root && this.router.routerState.snapshot.root.component) {
        this.logPageView(this.getTitleData(), this.getUrl());
      }

      document.querySelector("a.navbar-brand").textContent = "Azure Core";
    });
  }

  private getTitleFromRouteData(name: string, routeSnapshot = this.router.routerState.snapshot.root): string {
    let value = (routeSnapshot.data && routeSnapshot.data[name]) || null;
    if (routeSnapshot.firstChild) {
      value = this.getTitleFromRouteData(name, routeSnapshot.firstChild) || value;
    }
    return value;
  }

  public logPageView(
    name: string,
    url?: string,
    properties?: { [key: string]: string },
    measurements?: { [key: string]: number },
    duration?: number
  ) {
    const pageView: IPageViewTelemetry = {
      name: name,
      uri: url,
      properties: { ...properties, ...measurements, duration },
    };
    this.appInsights.trackPageView(pageView);
  }

  private getTitleData(): string {
    const title = this.getTitleFromRouteData("title");
    return (title && `${title} - Region Access and Quota`) || "Region Access and Quota";
  }

  private getUrl(): string {
    return location.href.split(location.hash || "#")[0];
  }
}
